import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, Textarea,
  FormControl, Box, Text, Badge, useToast, Flex, IconButton, Wrap, WrapItem, Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiClipboard, FiChevronUp, FiChevronDown, FiSearch, FiX, FiFile, FiDownload, FiSave, FiPaperclip } from 'react-icons/fi';
import api from '../api';
import FileUpload from './FileUpload';
import DocumentsModal from './DocumentsModal';
import { useAuth } from '../contexts/AuthContext';

function EyebrowLabel({ children }) {
  return (
    <Box bg="gray.800" color="white" px={1.5} py={0.5} borderRadius="sm" mb={1} display="inline-block">
      <Text fontSize="8px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" lineHeight="1">{children}</Text>
    </Box>
  );
}

// ── SmartCompanySearch ───────────────────────────────────────────────────────
// Shows assigned companies as a dropdown first; falls back to full-DB search.
function SmartCompanySearch({ value, suggested = [], onSelect, onClear }) {
  const [mode, setMode]   = useState('idle');   // idle | dropdown | search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    try {
      const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}&limit=10`);
      setResults(data);
    } catch(e) { setResults([]); }
  }, []);

  useEffect(() => {
    if (mode === 'search') {
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => doSearch(query), 280);
    }
    return () => clearTimeout(searchTimer.current);
  }, [query, mode, doSearch]);

  useEffect(() => {
    if (mode !== 'idle') setTimeout(() => inputRef.current?.focus(), 50);
  }, [mode]);

  const pick = (co) => {
    onSelect(co);
    setMode('idle'); setQuery(''); setResults([]);
  };

  if (mode === 'idle') {
    return (
      <Flex align="center" gap={1} minH="24px" cursor="pointer"
        onClick={() => setMode(suggested.length ? 'dropdown' : 'search')}
        _hover={{ bg: 'blue.50', borderRadius: 'sm' }} px={1} py={0.5}>
        {value
          ? <><Text fontSize="11px" flex={1} noOfLines={1}>{value}</Text>
              <FiX size={11} onClick={e => { e.stopPropagation(); onClear && onClear(); }} /></>
          : <Text fontSize="11px" color="gray.400">
              {suggested.length ? 'Select company…' : 'Search company…'}
            </Text>
        }
      </Flex>
    );
  }

  if (mode === 'dropdown') {
    return (
      <Box position="relative">
        <Select size="xs" fontSize="11px" autoFocus value=""
          onChange={e => {
            if (e.target.value === '__search__') { setMode('search'); return; }
            const co = suggested.find(c => (c.company_id || c.id) === e.target.value)
                    || suggested.find(c => c.id === e.target.value);
            if (co) pick({ id: co.company_id || co.id, name: co.name });
          }}
          onBlur={() => setTimeout(() => setMode('idle'), 200)}>
          <option value="">— Assigned companies —</option>
          {suggested.map(c => (
            <option key={c.company_id || c.id} value={c.company_id || c.id}>{c.name}</option>
          ))}
          <option value="__search__">🔍 Search all companies…</option>
        </Select>
      </Box>
    );
  }

  // search mode
  return (
    <Box position="relative">
      <Input ref={inputRef} size="xs" placeholder="Type to search companies…" value={query}
        onChange={e => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setMode('idle'), 200)} />
      {results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
          borderRadius="md" boxShadow="md" zIndex={9999} maxH="160px" overflowY="auto">
          {results.map(r => (
            <Box key={r.id} px={2} py={1.5} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
              onMouseDown={() => pick({ id: r.id, name: r.name })}>
              {r.name}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── SmartContactSearch ───────────────────────────────────────────────────────
// Priority: assigned contacts → company contacts (if companyId set) → full search
function SmartContactSearch({ companyId, value, suggested = [], onSelect, onClear }) {
  const [mode, setMode]         = useState('idle');
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [compContacts, setCC]   = useState([]);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  // Fetch contacts for the currently selected company
  useEffect(() => {
    if (!companyId) { setCC([]); return; }
    api.get(`/contacts?company_id=${companyId}`)
      .then(({ data }) => setCC(data))
      .catch(() => setCC([]));
  }, [companyId]);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    try {
      const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    } catch(e) { setResults([]); }
  }, []);

  useEffect(() => {
    if (mode === 'search') {
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => doSearch(query), 280);
    }
    return () => clearTimeout(searchTimer.current);
  }, [query, mode, doSearch]);

  useEffect(() => {
    if (mode !== 'idle') setTimeout(() => inputRef.current?.focus(), 50);
  }, [mode]);

  const pick = (ct) => { onSelect(ct); setMode('idle'); setQuery(''); setResults([]); };

  // Compute what to show in the dropdown
  const dropdownOptions = companyId && compContacts.length
    ? compContacts   // company-filtered
    : suggested;     // task/project-assigned

  const dropdownLabel = companyId && compContacts.length
    ? '— Contacts for this company —'
    : '— Assigned contacts —';

  if (mode === 'idle') {
    return (
      <Flex align="center" gap={1} minH="24px" cursor="pointer"
        onClick={() => setMode(dropdownOptions.length ? 'dropdown' : 'search')}
        _hover={{ bg: 'blue.50', borderRadius: 'sm' }} px={1} py={0.5}>
        {value
          ? <><Text fontSize="11px" flex={1} noOfLines={1}>{value}</Text>
              <FiX size={11} onClick={e => { e.stopPropagation(); onClear && onClear(); }} /></>
          : <Text fontSize="11px" color="gray.400">
              {dropdownOptions.length ? 'Select contact…' : 'Search contact…'}
            </Text>
        }
      </Flex>
    );
  }

  if (mode === 'dropdown') {
    return (
      <Box position="relative">
        <Select size="xs" fontSize="11px" autoFocus value=""
          onChange={e => {
            if (e.target.value === '__search__') { setMode('search'); return; }
            const ct = dropdownOptions.find(c => (c.contact_id || c.id) === e.target.value)
                    || dropdownOptions.find(c => c.id === e.target.value);
            if (ct) pick({
              id: ct.contact_id || ct.id,
              first_name: ct.first_name, last_name: ct.last_name,
              company_id: ct.company_id, company_name: ct.company_name || ct.name || '',
            });
          }}
          onBlur={() => setTimeout(() => setMode('idle'), 200)}>
          <option value="">{dropdownLabel}</option>
          {dropdownOptions.map(c => {
            const id = c.contact_id || c.id;
            const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
            return <option key={id} value={id}>{name}{c.company_name ? ` — ${c.company_name}` : ''}</option>;
          })}
          <option value="__search__">🔍 Search all contacts…</option>
        </Select>
      </Box>
    );
  }

  // search mode
  return (
    <Box position="relative">
      <Input ref={inputRef} size="xs" placeholder="Type to search contacts…" value={query}
        onChange={e => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setMode('idle'), 200)} />
      {results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
          borderRadius="md" boxShadow="md" zIndex={9999} maxH="160px" overflowY="auto">
          {results.map(r => (
            <Box key={r.id} px={2} py={1.5} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
              onMouseDown={() => pick({ id: r.id, first_name: r.first_name, last_name: r.last_name, company_id: r.company_id, company_name: r.company_name || '' })}>
              {r.first_name} {r.last_name || ''}
              {r.company_name && <Text as="span" color="gray.400"> — {r.company_name}</Text>}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── SmartUserSearch ──────────────────────────────────────────────────────────
function SmartUserSearch({ value, suggested = [], allUsers = [], onSelect, onClear }) {
  const opts = suggested.length ? suggested : allUsers;
  return (
    <Select size="xs" fontSize="11px" value={value ? (suggested.find(u => u.display_name === value || u.username === value)?.user_id
        || allUsers.find(u => u.display_name === value || u.username === value)?.id || '') : ''}
      onChange={e => {
        if (!e.target.value) { onClear && onClear(); return; }
        const u = opts.find(u => (u.user_id || u.id) === e.target.value);
        if (u) onSelect({ id: u.user_id || u.id, display_name: u.display_name || u.username });
      }}>
      <option value="">— No user —</option>
      {opts.map(u => <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.display_name || u.username}</option>)}
    </Select>
  );
}

// ── PurposeSelect ────────────────────────────────────────────────────────────
// Fixed: uses inline form instead of nested Modal (avoids Chakra focus-trap bug)
function PurposeSelect({ purposeId, onSelect }) {
  const [purposes, setPurposes] = useState([]);
  const [adding, setAdding]     = useState(false);
  const [newName, setNewName]   = useState('');
  const [saving, setSaving]     = useState(false);
  const newInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    api.get('/comm-purposes').then(({ data }) => setPurposes(data)).catch(() => {});
  }, []);

  // Deferred focus — avoids conflicting with Chakra Modal's focus trap
  useEffect(() => {
    if (adding) {
      const t = setTimeout(() => newInputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [adding]);

  const handleChange = (e) => {
    if (e.target.value === '__new__') {
      setAdding(true); setNewName('');
      return;
    }
    const id = e.target.value || null;
    onSelect(id, id ? purposes.find(p => p.id === id)?.name : '');
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/comm-purposes', { name: newName.trim() });
      setPurposes(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      onSelect(data.id, data.name);
      setAdding(false); setNewName('');
      toast({ title: `"${data.name}" added`, status: 'success', duration: 1500 });
    } catch(err) {
      toast({ title: err.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  if (adding) {
    return (
      <HStack spacing={1}>
        <Input ref={newInputRef} size="xs" flex={1} placeholder="New purpose name…" value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setAdding(false); }} />
        <Button size="xs" colorScheme="teal" isLoading={saving} onClick={handleCreate}>Add</Button>
        <IconButton size="xs" icon={<FiX />} variant="ghost" onClick={() => setAdding(false)} aria-label="Cancel" />
      </HStack>
    );
  }

  return (
    <Select size="xs" fontSize="11px" value={purposeId || ''} onChange={handleChange}>
      <option value="">— No purpose —</option>
      {purposes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      <option value="__new__">+ Add new purpose…</option>
    </Select>
  );
}

// ── Main CommunicationsModal ─────────────────────────────────────────────────
export default function CommunicationsModal({ projectId, taskId, subTaskId, subTaskTitle, isOpen, onClose }) {
  const [comms, setComms]               = useState([]);
  const [showForm, setShowForm]         = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);

  // recipients: [{role:'sender'|'to'|'cc'|'bcc', contact_id, user_id, contact_name, email_address}]
  const { user: currentUser } = useAuth();
  // Default the sender (From) to the logged-in user. They can click the cross to
  // remove this and then choose a Contact or a User instead.
  const defaultSender = () => currentUser ? {
    user_id: currentUser.id,
    contact_id: null,
    contact_name: currentUser.display_name || currentUser.name || currentUser.username || currentUser.email || 'Me',
    email_address: currentUser.email || null,
  } : null;
  const emptyRecipients = () => ({ sender: defaultSender(), to: [null], cc: [], bcc: [] });

  const [form, setForm] = useState({
    subject: '', body: '', direction: 'outbound', method: 'email',
    comm_date: new Date().toISOString().split('T')[0],
    comm_time: new Date().toTimeString().slice(0, 5),
    company_id: null, company_name: '', contact_id: null, contact_name: '',
    purpose_id: null, purpose_name: '', outgoing_status: 'draft', user_id: null, user_name: '',
    recipients: emptyRecipients(),
  });
  const [saving, setSaving]             = useState(false);
  const [editForm, setEditForm]         = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showPaste, setShowPaste]       = useState(false);
  const [pasteForm, setPasteForm]       = useState({ raw_text: '', company_id: null, contact_ids: [], user_ids: [], subject: '' });
  const [pasteFiles, setPasteFiles]     = useState([]);   // File[] to attach to the pasted email
  const pasteFileRef = useRef(null);
  const [pasting, setPasting]           = useState(false);
  const [allUsers, setAllUsers]         = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [assignedContacts, setAssignedContacts]   = useState([]);
  const [assignedUsers, setAssignedUsers]         = useState([]);
  // Inbox-email-specific state: when the selected row is a synthetic inbox email
  // (id starts with "inbox_"), the row only carries headers/body — attachments live
  // on the underlying email_messages record and must be loaded via /email/messages/:uuid.
  // Loaded lazily on selection so the comms list itself stays light.
  const [inboxAttachments, setInboxAttachments] = useState([]);
  const [savingToDocs, setSavingToDocs] = useState(false);
  // "Save attachment to documents": opens the STANDARD document form with
  // the file PRESELECTED (fileId — already uploaded, no re-upload); the
  // user assigns name, description, users/contacts as normal.
  const [docPrefill, setDocPrefill] = useState(null);        // initialDoc for DocumentsModal
  const [attachChooser, setAttachChooser] = useState(null);  // [{id,name,size}] when multiple

  const prefillFromFile = (f) => {
    const nm = String(f.original_name || f.name || 'Attachment');
    setDocPrefill({
      fileId: f.id || f.uuid,
      fileName: nm,
      fileSize: f.size || f.size_bytes || null,
      name: nm.replace(/\.[^.]+$/, ''),
    });
    setAttachChooser(null);
  };

  const openSaveToDocuments = async () => {
    if (!selectedComm) return;
    setSavingToDocs(true);
    try {
      const { data } = await api.get(`/files?entity_type=communication&entity_id=${selectedComm.id}`);
      const files = Array.isArray(data) ? data : (data?.data || []);
      if (!files.length) { toast({ title: 'No attachments found on this communication', status: 'info', duration: 2200 }); return; }
      if (files.length === 1) prefillFromFile(files[0]);
      else setAttachChooser(files);
    } catch (e) {
      toast({ title: 'Could not load attachments', status: 'error', duration: 2200 });
    } finally { setSavingToDocs(false); }
  };
  const commDetailRef = useRef(null);
  const wheelCooldown = useRef(false);
  const toast = useToast();

  const navigateComm = useCallback((dir) => {
    if (!selectedComm || !comms.length) return;
    const idx = comms.findIndex(c => c.id === selectedComm.id);
    if (idx === -1) return;
    const newIdx = idx + dir;
    if (newIdx >= 0 && newIdx < comms.length) { setSelectedComm(comms[newIdx]); setEditForm(null); }
  }, [selectedComm, comms]);

  const commIndex = selectedComm ? comms.findIndex(c => c.id === selectedComm.id) : -1;

  useEffect(() => {
    const el = commDetailRef.current;
    if (!el || !selectedComm) return;
    const handleWheel = (e) => {
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      if (el.scrollHeight > el.clientHeight && !atTop && e.deltaY < 0) return;
      if (el.scrollHeight > el.clientHeight && !atBottom && e.deltaY > 0) return;
      if (wheelCooldown.current) return;
      wheelCooldown.current = true;
      setTimeout(() => { wheelCooldown.current = false; }, 300);
      e.preventDefault(); e.stopPropagation();
      navigateComm(e.deltaY > 0 ? 1 : -1);
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [selectedComm, navigateComm]);

  // Load the underlying email's attachments whenever an inbox-email row is
  // selected. Skipped for normal comms (their attachments are rendered by
  // the FileUpload component below using its own /files lookup). The email
  // uuid is on the synthetic row via the merge in the comms route — we use
  // it directly with /email/messages/:uuid.
  useEffect(() => {
    if (!selectedComm || !selectedComm.is_inbox_email) { setInboxAttachments([]); return; }
    const emailUuid = selectedComm.uuid || (typeof selectedComm.id === 'string' && selectedComm.id.startsWith('inbox_')
      ? selectedComm.id.slice(6) : null);
    if (!emailUuid) { setInboxAttachments([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/email/messages/${emailUuid}`);
        if (!cancelled) setInboxAttachments(data?.attachments || []);
      } catch(e) { if (!cancelled) setInboxAttachments([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedComm]);

  // Save one or all email attachments to project_documents for this task.
  // fileId omitted = copy every attachment on this email.
  const saveInboxAttachmentToDocs = async (fileId = null) => {
    if (!selectedComm?.is_inbox_email) return;
    const emailUuid = selectedComm.uuid || (typeof selectedComm.id === 'string' && selectedComm.id.startsWith('inbox_')
      ? selectedComm.id.slice(6) : null);
    if (!emailUuid) return;
    setSavingToDocs(true);
    try {
      // Server falls back to email's own stored task/project if these are omitted.
      const body = {};
      if (taskId) body.task_id = taskId;
      if (projectId) body.project_id = projectId;
      const { data } = await api.post(`/email/messages/${emailUuid}/copy-to-documents`, body);
      const copied = data?.copied || 0;
      const skipped = data?.skipped || 0;
      toast({
        title: copied > 0
          ? `${copied} attachment${copied === 1 ? '' : 's'} saved to documents`
          : (skipped > 0 ? 'Already in documents' : 'Nothing to save'),
        status: copied > 0 ? 'success' : 'info',
        duration: 2000,
      });
    } catch(e) {
      toast({ title: 'Save to documents failed', status: 'error', duration: 2000 });
    } finally {
      setSavingToDocs(false);
    }
  };

  // Save one or all attachments from a regular communication to documents.
  // fileIds omitted = copy every file. fileIds present = restrict to subset.
  const saveCommAttachmentsToDocs = async (fileIds = null) => {
    if (!selectedComm || selectedComm.is_inbox_email) return;
    setSavingToDocs(true);
    try {
      const body = {};
      if (Array.isArray(fileIds) && fileIds.length > 0) body.file_ids = fileIds;
      const { data } = await api.post(`/communications/${selectedComm.id}/copy-files-to-documents`, body);
      const copied = data?.copied || 0;
      const skipped = data?.skipped || 0;
      toast({
        title: copied > 0
          ? `${copied} file${copied === 1 ? '' : 's'} saved to documents`
          : (skipped > 0 ? 'Already in documents' : 'No files to save'),
        status: copied > 0 ? 'success' : 'info',
        duration: 2000,
      });
    } catch(e) {
      toast({ title: 'Save to documents failed', status: 'error', duration: 2000 });
    } finally {
      setSavingToDocs(false);
    }
  };

  const fetchComms = async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const subParam = subTaskId ? `&sub_task_id=${subTaskId}` : '';
      const { data } = await api.get(`/communications?project_id=${projectId}${taskParam}${subParam}`);
      setComms(data); return data;
    } catch(e) { return []; }
  };

  const fetchUsers = async () => {
    try { const { data } = await api.get('/calendar/users'); setAllUsers(data); } catch(e) {}
  };

  const fetchAssigned = async () => {
    try {
      // Always fetch project-level; also fetch task-level if taskId is set
      const promises = [
        api.get(`/projects/${projectId}/companies`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/contacts`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/users`).catch(() => ({ data: [] })),
      ];
      if (taskId) {
        promises.push(
          api.get(`/tasks/${taskId}/companies`).catch(() => ({ data: [] })),
          api.get(`/tasks/${taskId}/contacts`).catch(() => ({ data: [] })),
          api.get(`/tasks/${taskId}/users`).catch(() => ({ data: [] })),
        );
      }
      const [pCoRes, pCtRes, pURes, tCoRes, tCtRes, tURes] = await Promise.all(promises);

      // Merge, deduplicating by ID
      const mergeById = (arr1, arr2, idFn) => {
        const combined = [...(arr1 || [])];
        const seen = new Set(combined.map(idFn));
        for (const item of (arr2 || [])) {
          const id = idFn(item);
          if (id && !seen.has(id)) { seen.add(id); combined.push(item); }
        }
        return combined;
      };

      let companies = mergeById(pCoRes.data, taskId ? tCoRes?.data : [], c => c.company_id || c.id);
      const contacts  = mergeById(pCtRes.data, taskId ? tCtRes?.data : [], c => c.id || c.contact_id);
      const users     = mergeById(pURes.data,  taskId ? tURes?.data  : [], u => u.user_id || u.id);

      // Also include suppliers added to the project (and task) so they are
      // selectable as the company on a communication.
      try {
        const supPromises = [ api.get(`/projects/${projectId}/suppliers`).catch(() => ({ data: [] })) ];
        if (taskId) supPromises.push(api.get(`/tasks/${taskId}/suppliers`).catch(() => ({ data: [] })));
        const supRes = await Promise.all(supPromises);
        let suppliers = [];
        supRes.forEach(r => { suppliers = mergeById(suppliers, r.data || [], c => c.company_id || c.id); });
        companies = mergeById(companies, suppliers, c => c.company_id || c.id);
      } catch(e) {}

      setAssignedCompanies(companies);
      setAssignedContacts(contacts);
      setAssignedUsers(users);
      return { companies, contacts, users };
    } catch(e) { return { companies: [], contacts: [], users: [] }; }
  };

  // Smart defaults from recent comm pattern
  const buildDefaults = (latestComms, assigned) => {
    const out = { company_id: null, company_name: '', contact_id: null, contact_name: '', user_id: null, user_name: '' };
    const c0 = latestComms[0]; // most recent comm
    const c1 = latestComms[1]; // second most recent

    // Contact logic:
    // Pre-populate if there's only one comm OR if last two were to the same contact.
    // If last two were to DIFFERENT contacts, leave blank (user chooses from dropdown).
    const lastTwoDifferentContacts = c0?.contact_id && c1?.contact_id && c0.contact_id !== c1.contact_id;

    if (c0?.contact_id && !lastTwoDifferentContacts) {
      // Pre-populate with the most recent comm's contact
      out.contact_id   = c0.contact_id;
      out.contact_name = c0.contact_first_name
        ? `${c0.contact_first_name} ${c0.contact_last_name || ''}`.trim()
        : '';
      // Company from the comm itself, or from the contact's company
      out.company_id   = c0.company_id || null;
      out.company_name = c0.company_name || c0.contact_company_name || '';
    } else if (!c0?.contact_id && assigned.contacts.length === 1) {
      // No recent comms with a contact, but only one assigned → pre-select it
      const ct = assigned.contacts[0];
      out.contact_id   = ct.id || ct.contact_id;
      out.contact_name = `${ct.first_name || ''} ${ct.last_name || ''}`.trim();
      if (ct.company_id) {
        out.company_id   = ct.company_id;
        out.company_name = ct.company_name || '';
      }
    }

    // Company: if not set by contact, check last comm or single assigned
    if (!out.company_id) {
      if (c0?.company_id && !lastTwoDifferentContacts) {
        out.company_id   = c0.company_id;
        out.company_name = c0.company_name || '';
      } else if (assigned.companies.length === 1) {
        out.company_id   = assigned.companies[0].company_id || assigned.companies[0].id;
        out.company_name = assigned.companies[0].name || '';
      }
    }

    // User: use most recent comm's user, or single assigned user
    const recentWithUser = latestComms.find(c => c.user_id);
    if (recentWithUser) {
      out.user_id   = recentWithUser.user_id;
      out.user_name = recentWithUser.assigned_user_name || '';
    } else if (assigned.users.length === 1) {
      const u = assigned.users[0];
      out.user_id   = u.user_id || u.id;
      out.user_name = u.display_name || u.username || '';
    }

    return out;
  };

  useEffect(() => {
    if (isOpen && projectId) {
      (async () => {
        const [commsData, assigned] = await Promise.all([fetchComms(), fetchAssigned()]);
        await fetchUsers();
        const defaults = buildDefaults(commsData, assigned);
        setForm({
          subject: '', body: '', direction: 'outbound', method: 'email',
          comm_date: new Date().toISOString().split('T')[0],
          comm_time: new Date().toTimeString().slice(0, 5),
          purpose_id: null, purpose_name: '', outgoing_status: 'draft',
          recipients: emptyRecipients(),
          ...defaults,
        });
        setShowForm(false); setShowPaste(false); setSelectedComm(null); setEditForm(null);
      })();
    }
  }, [isOpen, projectId]); // eslint-disable-line

  const resetForm = () => {
    const defaults = buildDefaults(comms, { companies: assignedCompanies, contacts: assignedContacts, users: assignedUsers });
    setForm({
      subject: '', body: '', direction: 'outbound', method: 'email',
      comm_date: new Date().toISOString().split('T')[0],
      comm_time: new Date().toTimeString().slice(0, 5),
      purpose_id: null, purpose_name: '', outgoing_status: 'draft',
      recipients: emptyRecipients(),
      ...defaults,
    });
  };

  // Convert recipients array from API into the keyed form structure
  const recsToForm = (recs) => {
    const out = emptyRecipients();
    if (!recs || recs.length === 0) return out;
    recs.forEach(r => {
      const entry = {
        contact_id: r.contact_id || null,
        user_id: r.user_id || null,
        contact_name: r.contact_name || (r.first_name ? `${r.first_name} ${r.last_name || ''}`.trim() : (r.user_name || null)),
        email_address: r.email_address || r.contact_email || r.user_email || null,
      };
      if (r.role === 'sender') out.sender = entry;
      else if (r.role === 'to') out.to.push(entry);
      else if (r.role === 'cc') out.cc.push(entry);
      else if (r.role === 'bcc') out.bcc.push(entry);
    });
    if (out.to.length === 0) out.to = [null];
    return out;
  };

  // Convert form recipients structure into API array
  const formToRecs = (recipients, method) => {
    const recs = [];
    const push = (role, entry) => {
      if (!entry) return;
      if (entry.contact_id || entry.user_id || entry.email_address || entry.contact_name) {
        recs.push({ role, contact_id: entry.contact_id || null, user_id: entry.user_id || null, email_address: entry.email_address || null, contact_name: entry.contact_name || null });
      }
    };
    push('sender', recipients.sender);
    (recipients.to || []).forEach(e => push('to', e));
    if (method === 'email') {
      (recipients.cc || []).forEach(e => push('cc', e));
      (recipients.bcc || []).forEach(e => push('bcc', e));
    }
    return recs;
  };

  const startEdit = async (comm) => {
    let recs;
    try { const { data } = await api.get(`/communications/${comm.id}/recipients`); recs = data; } catch(e) { recs = []; }
    // If no recipients table yet, derive from legacy contact_id
    if (recs.length === 0 && comm.contact_id) {
      recs = [{ role: 'to', contact_id: comm.contact_id,
        contact_name: comm.contact_first_name ? `${comm.contact_first_name} ${comm.contact_last_name||''}`.trim() : '' }];
    }
    setEditForm({
      subject: comm.subject || '', body: comm.body || '',
      direction: comm.direction || 'outbound', method: comm.method || 'email',
      comm_date: comm.comm_date ? comm.comm_date.split('T')[0] : '',
      comm_time: comm.comm_time ? comm.comm_time.slice(0,5) : '',
      company_id: comm.company_id, company_name: comm.company_name || comm.contact_company_name || '',
      contact_id: comm.contact_id,
      contact_name: comm.contact_first_name ? `${comm.contact_first_name} ${comm.contact_last_name || ''}`.trim() : '',
      purpose_id: comm.purpose_id, purpose_name: comm.purpose_name || '',
      outgoing_status: comm.outgoing_status || 'draft',
      recipients: recsToForm(recs),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Derive legacy contact_id from first 'to' recipient for backwards compat
      const toRec = form.recipients?.to?.find(r => r?.contact_id) || null;
      const senderRec = form.recipients?.sender;
      await api.post('/communications', {
        project_id: projectId, task_id: taskId || null,
        sub_task_id: subTaskId || null,
        company_id: form.company_id,
        contact_id: toRec?.contact_id || form.contact_id || null,
        comm_date: form.comm_date, comm_time: form.comm_time || null,
        subject: form.subject, body: form.body,
        direction: form.direction, method: form.method,
        purpose_id: form.purpose_id, user_id: form.user_id || null,
        recipients: formToRecs(form.recipients || emptyRecipients(), form.method),
      });
      await fetchComms();
      setShowForm(false); resetForm();
      toast({ title: 'Communication logged', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed to save', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editForm || !selectedComm) return;
    setSaving(true);
    try {
      const toRec = editForm.recipients?.to?.find(r => r?.contact_id) || null;
      const { data } = await api.put(`/communications/${selectedComm.id}`, {
        company_id: editForm.company_id,
        contact_id: toRec?.contact_id || editForm.contact_id || null,
        comm_date: editForm.comm_date, comm_time: editForm.comm_time || null,
        subject: editForm.subject, body: editForm.body,
        direction: editForm.direction, method: editForm.method,
        purpose_id: editForm.purpose_id, outgoing_status: editForm.outgoing_status || null,
        recipients: formToRecs(editForm.recipients || emptyRecipients(), editForm.method),
      });
      await fetchComms(); setSelectedComm(data); setEditForm(null);
      toast({ title: 'Updated', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/communications/${deleteConfirm.id}`, { data: { reason: deleteReason || null } });
      toast({ title: 'Deleted', status: 'info', duration: 1500 });
      setDeleteConfirm(null); setDeleteReason(''); setSelectedComm(null); setEditForm(null);
      await fetchComms();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handlePaste = async () => {
    if (!pasteForm.raw_text.trim()) { toast({ title: 'Paste email content first', status: 'warning', duration: 2000 }); return; }
    setPasting(true);
    try {
      // Upload any attached files first, collecting their ids.
      let fileIds = [];
      if (pasteFiles.length > 0) {
        for (const file of pasteFiles) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('folder', 'communications');
            fd.append('entity_type', 'communication');
            const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (data?.id) fileIds.push(data.id);
          } catch (e) { toast({ title: `Upload failed: ${file.name}`, status: 'error', duration: 2500 }); }
        }
      }
      const { data } = await api.post('/communications/paste', {
        project_id: projectId, task_id: taskId || null,
        company_id: pasteForm.company_id || null,
        contact_ids: pasteForm.contact_ids, user_ids: pasteForm.user_ids,
        raw_text: pasteForm.raw_text, subject_override: pasteForm.subject || null,
        file_ids: fileIds,
      });
      await fetchComms();
      setShowPaste(false);
      setPasteForm({ raw_text: '', company_id: null, contact_ids: [], user_ids: [], subject: '' });
      setPasteFiles([]);
      const docBits = data.docs ? [
        data.docs.copied ? `${data.docs.copied} to documents` : '',
        data.docs.versioned ? `${data.docs.versioned} version-updated` : '',
      ].filter(Boolean).join(', ') : '';
      toast({
        title: `${data.added} messages added, ${data.skipped} duplicates skipped` +
          (data.files_attached ? ` · ${data.files_attached} file${data.files_attached === 1 ? '' : 's'} attached` : '') +
          (docBits ? ` (${docBits})` : ''),
        status: 'success', duration: 4000,
      });
    } catch(e) { toast({ title: 'Paste failed', status: 'error', duration: 2000 }); }
    finally { setPasting(false); }
  };

  const methodColors = { email:'blue', phone:'green', meeting:'purple', web_lead:'cyan', chatbot:'orange', whatsapp:'green', in_person:'pink', media_press:'red', social_media:'teal', other:'gray' };
  const methodLabels = { email:'Email', phone:'Phone', meeting:'Meeting', web_lead:'Web Lead', chatbot:'Chat Bot', whatsapp:'WhatsApp', in_person:'In Person', media_press:'Media/Press', social_media:'Social Media', other:'Other' };
  const ml = (m) => methodLabels[m] || m;

  const formatDateTime = (c) => {
    const d = new Date(c.comm_date).toLocaleDateString('en-GB');
    const t = c.comm_time ? c.comm_time.slice(0,5) : '';
    return t ? `${d} ${t}` : d;
  };

  const contactDisplay = (c) => {
    const parts = [];
    if (c.contact_first_name) parts.push(`${c.contact_first_name} ${c.contact_last_name || ''}`.trim());
    const co = c.company_name || c.contact_company_name;
    if (co) parts.push(co);
    return parts.join(' — ');
  };

  // ── Method-aware recipient labels ────────────────────────────────────────
  const recipientConfig = (method) => {
    switch (method) {
      case 'email':       return { sender: 'Sender (From)', to: 'To',          cc: 'CC', bcc: 'BCC', multiTo: true,  hasCcBcc: true };
      case 'phone':       return { sender: 'Caller',        to: 'Called',      cc: null, bcc: null,  multiTo: false, hasCcBcc: false };
      case 'whatsapp':    return { sender: 'From',          to: 'To',          cc: null, bcc: null,  multiTo: false, hasCcBcc: false };
      case 'meeting':     return { sender: 'Organiser',     to: 'Attendees',   cc: null, bcc: null,  multiTo: true,  hasCcBcc: false };
      case 'in_person':   return { sender: 'Met With',      to: null,          cc: null, bcc: null,  multiTo: false, hasCcBcc: false };
      case 'media_press': return { sender: 'Contact',       to: 'Publication', cc: null, bcc: null,  multiTo: false, hasCcBcc: false };
      default:            return { sender: 'Contact',       to: null,          cc: null, bcc: null,  multiTo: false, hasCcBcc: false };
    }
  };

  // A single contact picker (search-as-you-type, raw email fallback)
  const RecipientEntry = ({ value, onChange, onRemove, placeholder, showRemove }) => {
    const [q, setQ] = React.useState('');
    const [results, setResults] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const timer = useRef(null);
    React.useEffect(() => {
      clearTimeout(timer.current);
      if (!q.trim()) { setResults([]); return; }
      timer.current = setTimeout(async () => {
        try { const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}&limit=8`); setResults(data); setOpen(true); } catch(e) {}
      }, 220);
      return () => clearTimeout(timer.current);
    }, [q]);
    const display = value ? (value.contact_name || value.email_address || '') : '';
    if (display && !open) {
      return (
        <Flex align="center" gap={1} bg="gray.50" borderRadius="sm" px={1.5} py={0.5}
          border="1px" borderColor="gray.200" fontSize="11px" minH="24px">
          <Text flex={1} noOfLines={1} color="gray.700">{display}</Text>
          {value.email_address && !value.contact_id && (
            <Text fontSize="9px" color="gray.400" ml={1}>&lt;{value.email_address}&gt;</Text>
          )}
          <Box as="span" cursor="pointer" onClick={() => { onChange(null); setQ(''); }}>
            <FiX size={11} />
          </Box>
          {showRemove && (
            <Box as="span" cursor="pointer" color="red.400" ml={1} onClick={onRemove}>
              <FiTrash2 size={10} />
            </Box>
          )}
        </Flex>
      );
    }
    return (
      <Box position="relative">
        <Flex gap={1}>
          <Input size="xs" flex={1} placeholder={placeholder || 'Search contact or type email…'} value={q}
            onChange={e => setQ(e.target.value)}
            onBlur={() => setTimeout(() => { setOpen(false); }, 200)} autoComplete="off" />
          {q.includes('@') && (
            <Button size="xs" variant="outline" colorScheme="blue" flexShrink={0}
              onMouseDown={() => { onChange({ contact_id: null, contact_name: q, email_address: q }); setQ(''); setOpen(false); }}>
              Use email
            </Button>
          )}
          {showRemove && (
            <IconButton size="xs" icon={<FiTrash2 size={11} />} variant="ghost" colorScheme="red"
              onClick={onRemove} aria-label="Remove" />
          )}
        </Flex>
        {open && results.length > 0 && (
          <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
            borderRadius="md" boxShadow="md" zIndex={9999} maxH="140px" overflowY="auto">
            {results.map(r => (
              <Box key={r.id} px={2} py={1.5} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                onMouseDown={() => {
                  onChange({ contact_id: r.id, contact_name: `${r.first_name} ${r.last_name||''}`.trim(), email_address: r.email||null });
                  setQ(''); setOpen(false);
                }}>
                {r.first_name} {r.last_name||''}
                {r.company_name && <Text as="span" color="gray.400" fontSize="10px"> — {r.company_name}</Text>}
                {r.email && <Text as="span" color="gray.400" fontSize="9px" ml={1}>&lt;{r.email}&gt;</Text>}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Sender (From) — may be a Contact or a User. Defaults to the logged-in user;
  // click the cross to clear, then choose Contact or User to set a different sender.
  const SenderEntry = ({ value, onChange, placeholder }) => {
    const [mode, setMode] = React.useState(null); // null | 'contact' | 'user'
    const [q, setQ] = React.useState('');
    const [results, setResults] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const timer = useRef(null);
    React.useEffect(() => {
      if (mode !== 'contact') return;
      clearTimeout(timer.current);
      if (!q.trim()) { setResults([]); return; }
      timer.current = setTimeout(async () => {
        try { const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}&limit=8`); setResults(data); setOpen(true); } catch(e) {}
      }, 220);
      return () => clearTimeout(timer.current);
    }, [q, mode]);

    if (value && (value.contact_id || value.user_id || value.email_address || value.contact_name)) {
      const isUser = !!value.user_id;
      return (
        <Flex align="center" gap={1} bg={isUser ? 'blue.50' : 'gray.50'} borderRadius="sm" px={1.5} py={0.5}
          border="1px" borderColor={isUser ? 'blue.200' : 'gray.200'} fontSize="11px" minH="24px">
          <Badge colorScheme={isUser ? 'blue' : 'gray'} fontSize="8px">{isUser ? 'User' : 'Contact'}</Badge>
          <Text flex={1} noOfLines={1} color="gray.700">{value.contact_name || value.email_address}</Text>
          {value.email_address && <Text fontSize="9px" color="gray.400" ml={1}>&lt;{value.email_address}&gt;</Text>}
          <Box as="span" cursor="pointer" title="Remove sender" onClick={() => { onChange(null); setMode(null); setQ(''); }}>
            <FiX size={12} />
          </Box>
        </Flex>
      );
    }

    if (!mode) {
      return (
        <HStack spacing={2}>
          <Text fontSize="10px" color="gray.500">Set sender as:</Text>
          <Button size="xs" variant="outline" onClick={() => setMode('contact')}>Contact</Button>
          <Button size="xs" variant="outline" colorScheme="blue" onClick={() => setMode('user')}>User</Button>
        </HStack>
      );
    }

    if (mode === 'user') {
      return (
        <Flex gap={1} align="center">
          <Select size="xs" flex={1} placeholder="Select a user…" value="" autoFocus
            onChange={e => {
              const u = (allUsers || []).find(x => String(x.id) === e.target.value);
              if (u) { onChange({ user_id: u.id, contact_id: null, contact_name: u.display_name || u.username, email_address: u.email || null }); setMode(null); }
            }}>
            {(allUsers || []).map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
          </Select>
          <Button size="xs" variant="ghost" onClick={() => setMode(null)}>Back</Button>
        </Flex>
      );
    }

    return (
      <Box position="relative">
        <Flex gap={1}>
          <Input size="xs" flex={1} placeholder={placeholder || 'Search contact or type email…'} value={q} autoFocus
            onChange={e => setQ(e.target.value)} onBlur={() => setTimeout(() => setOpen(false), 200)} autoComplete="off" />
          {q.includes('@') && (
            <Button size="xs" variant="outline" colorScheme="blue" flexShrink={0}
              onMouseDown={() => { onChange({ contact_id: null, user_id: null, contact_name: q, email_address: q }); setMode(null); setQ(''); setOpen(false); }}>
              Use email
            </Button>
          )}
          <Button size="xs" variant="ghost" onClick={() => { setMode(null); setQ(''); setOpen(false); }}>Back</Button>
        </Flex>
        {open && results.length > 0 && (
          <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
            borderRadius="md" boxShadow="md" zIndex={9999} maxH="140px" overflowY="auto">
            {results.map(r => (
              <Box key={r.id} px={2} py={1.5} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                onMouseDown={() => { onChange({ contact_id: r.id, user_id: null, contact_name: `${r.first_name} ${r.last_name||''}`.trim(), email_address: r.email||null }); setMode(null); setQ(''); setOpen(false); }}>
                {r.first_name} {r.last_name||''}
                {r.company_name && <Text as="span" color="gray.400" fontSize="10px"> — {r.company_name}</Text>}
                {r.email && <Text as="span" color="gray.400" fontSize="9px" ml={1}>&lt;{r.email}&gt;</Text>}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Multi-entry recipient list (To / Attendees / CC / BCC)
  const RecipientList = ({ entries, onChange, label, minCount, placeholder }) => {
    const list = (entries && entries.length >= minCount) ? entries : [...(entries||[]), ...Array(Math.max(0, minCount-(entries||[]).length)).fill(null)];
    return (
      <VStack spacing={1} align="stretch">
        {list.map((entry, i) => (
          <RecipientEntry key={i} value={entry} placeholder={placeholder || `${label}…`}
            showRemove={list.length > minCount || (list.length === minCount && minCount === 0)}
            onChange={val => { const next=[...list]; next[i]=val; onChange(next.filter((e,j)=>e!==null||j<minCount)); }}
            onRemove={() => { const next=list.filter((_,j)=>j!==i); onChange(next.length>=minCount?next:[...next,null]); }} />
        ))}
        <Box as="button" fontSize="9px" color="blue.500" textAlign="left" cursor="pointer"
          onClick={() => onChange([...list, null])} _hover={{ textDecoration: 'underline' }}>
          + Add another {label}
        </Box>
      </VStack>
    );
  };

  // Method-aware recipients + company/purpose section
  const renderRecipientsSection = (fv, setFv) => {
    const cfg = recipientConfig(fv.method);
    const recs = fv.recipients || emptyRecipients();
    const setR = (key, val) => setFv(p => ({ ...p, recipients: { ...(p.recipients || emptyRecipients()), [key]: val } }));
    return (
      <VStack w="100%" spacing={2} align="stretch">
        <HStack w="100%" spacing={2}>
          <FormControl flex={1}>
            <EyebrowLabel>Company</EyebrowLabel>
            <SmartCompanySearch value={fv.company_name} suggested={assignedCompanies}
              onSelect={co => setFv(p => ({ ...p, company_id: co.id, company_name: co.name }))}
              onClear={() => setFv(p => ({ ...p, company_id: null, company_name: '' }))} />
          </FormControl>
          <FormControl flex={1}>
            <EyebrowLabel>Purpose</EyebrowLabel>
            <PurposeSelect purposeId={fv.purpose_id}
              onSelect={(id, name) => setFv(p => ({ ...p, purpose_id: id, purpose_name: name || '' }))} />
          </FormControl>
        </HStack>
        <FormControl>
          <EyebrowLabel>{cfg.sender}</EyebrowLabel>
          <SenderEntry value={recs.sender} placeholder={`${cfg.sender}…`} onChange={val => setR('sender', val)} />
        </FormControl>
        {cfg.to && (
          <FormControl>
            <EyebrowLabel>{cfg.to}</EyebrowLabel>
            {cfg.multiTo
              ? <RecipientList entries={recs.to} label={cfg.to} minCount={1} onChange={val => setR('to', val)} />
              : <RecipientEntry value={recs.to?.[0]||null} placeholder={`${cfg.to}…`}
                  onChange={val => setR('to', val ? [val] : [null])} />}
          </FormControl>
        )}
        {cfg.hasCcBcc && (
          <HStack w="100%" spacing={2} align="flex-start">
            <FormControl flex={1}>
              <EyebrowLabel>CC</EyebrowLabel>
              <RecipientList entries={recs.cc} label="CC" minCount={0} onChange={val => setR('cc', val)} />
            </FormControl>
            <FormControl flex={1}>
              <EyebrowLabel>BCC</EyebrowLabel>
              <RecipientList entries={recs.bcc} label="BCC" minCount={0} onChange={val => setR('bcc', val)} />
            </FormControl>
          </HStack>
        )}
      </VStack>
    );
  };



  const renderMethodRow = (fv, setFv) => (
    <HStack w="100%" spacing={2}>
      <FormControl flex={1}>
        <EyebrowLabel>Date</EyebrowLabel>
        <Input size="xs" type="date" value={fv.comm_date} onChange={e => setFv(p => ({ ...p, comm_date: e.target.value }))} />
      </FormControl>
      <FormControl flex={1}>
        <EyebrowLabel>Time</EyebrowLabel>
        <Input size="xs" type="time" value={fv.comm_time} onChange={e => setFv(p => ({ ...p, comm_time: e.target.value }))} />
      </FormControl>
      <FormControl flex={1}>
        <EyebrowLabel>Method</EyebrowLabel>
        <Select size="xs" value={fv.method} fontSize="11px" onChange={e => setFv(p => ({ ...p, method: e.target.value }))}>
          {['email','phone','meeting','web_lead','chatbot','whatsapp','in_person','media_press','social_media','other'].map(m => (
            <option key={m} value={m}>{ml(m)}</option>
          ))}
        </Select>
      </FormControl>
      <FormControl flex={1}>
        <EyebrowLabel>Direction</EyebrowLabel>
        <Select size="xs" value={fv.direction} fontSize="11px" onChange={e => setFv(p => ({ ...p, direction: e.target.value }))}>
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </Select>
      </FormControl>
      {fv.direction === 'outbound' && (
        <FormControl flex={1}>
          <EyebrowLabel>Status</EyebrowLabel>
          <Select size="xs" value={fv.outgoing_status || 'draft'} fontSize="11px" onChange={e => setFv(p => ({ ...p, outgoing_status: e.target.value }))}>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="reply_received">Reply Received</option>
          </Select>
        </FormControl>
      )}
    </HStack>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        {/* Width contract: 10% in from each side, centred. Instead of a
            computed width (w/maxW=80vw) — which the live environment proved
            able to defeat — BOTH edges are pinned to the viewport with
            fixed insets (left/right 10vw). The modal's width is therefore
            defined by its margins: it physically cannot extend past 90% of
            the screen no matter what the content does. overflowX:hidden
            clips anything that still tries, and overflowWrap:anywhere
            (inherited) breaks the non-breaking-space runs that pasted
            Outlook emails are full of, so text wraps instead of pushing. */}
        <ModalContent position="fixed" left="10vw" right="10vw" top="6vh"
          w="auto" maxW="none" my={0} maxH="88vh" overflowX="hidden"
          sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          <ModalHeader fontSize="md">
            <Flex align="center" justify="space-between" pr={8}>
              <Box>
                <Text>Communications</Text>
                {subTaskTitle && (
                  <Text fontSize="11px" color="orange.600" fontWeight="500">
                    ↳ Sub-task: {subTaskTitle}
                  </Text>
                )}
              </Box>
              <HStack spacing={1}>
                <Button size="xs" leftIcon={<FiClipboard />} variant={showPaste ? 'solid' : 'outline'} colorScheme="purple"
                  onClick={() => { setShowPaste(!showPaste); setShowForm(false); setSelectedComm(null); if (showPaste) setPasteFiles([]); }}>
                  {showPaste ? 'Cancel' : 'Paste'}
                </Button>
                <Button size="xs" leftIcon={<FiPlus />} variant={showForm ? 'solid' : 'outline'} colorScheme="blue"
                  onClick={() => { setShowForm(!showForm); setShowPaste(false); setSelectedComm(null); }}>
                  {showForm ? 'Cancel' : 'New'}
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>

            {/* New form */}
            {showForm && (
              <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
                <VStack spacing={2}>
                  {renderMethodRow(form, setForm)}
                  {renderRecipientsSection(form, setForm)}
                  {(form.company_name || form.contact_name || form.purpose_name) && (
                    <Flex w="100%" gap={2} fontSize="10px" flexWrap="wrap">
                      {form.company_name && <Badge colorScheme="teal" fontSize="9px">{form.company_name}</Badge>}
                      {form.contact_name && <Badge colorScheme="purple" fontSize="9px">{form.contact_name}</Badge>}
                      {form.purpose_name && <Badge colorScheme="yellow" fontSize="9px">{form.purpose_name}</Badge>}
                      <Text cursor="pointer" color="red.400" onClick={() => setForm(p => ({ ...p, company_id: null, company_name: '', contact_id: null, contact_name: '', purpose_id: null, purpose_name: '' }))}>clear</Text>
                    </Flex>
                  )}
                  <FormControl>
                    <EyebrowLabel>Subject</EyebrowLabel>
                    <Input size="xs" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
                  </FormControl>
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={3}>
                      <EyebrowLabel>Content</EyebrowLabel>
                      <Textarea size="xs" rows={3} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} />
                    </FormControl>
                    <FormControl flex={1}>
                      <EyebrowLabel>Assign User</EyebrowLabel>
                      <SmartUserSearch
                        value={form.user_name} suggested={assignedUsers} allUsers={allUsers}
                        onSelect={u => setForm(p => ({ ...p, user_id: u.id, user_name: u.display_name || '' }))}
                        onClear={() => setForm(p => ({ ...p, user_id: null, user_name: '' }))}
                      />
                    </FormControl>
                  </HStack>
                  <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                    onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                    Save Communication
                  </Button>
                </VStack>
              </Box>
            )}

            {/* Paste form */}
            {showPaste && !showForm && (
              <Box bg="purple.50" p={5} borderRadius="lg" mb={5} border="1px" borderColor="purple.200">
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="15px" fontWeight="700" color="purple.800">Paste an Email Chain</Text>
                    <Text fontSize="12px" color="purple.600" mt={0.5}>
                      Each message is parsed and logged individually; duplicates are skipped. Attach any related files below — they're saved to this task's documents, and a same-named file becomes a new version automatically.
                    </Text>
                  </Box>
                  <HStack w="100%" spacing={4} align="flex-start">
                    <FormControl flex={1}>
                      <EyebrowLabel>Company</EyebrowLabel>
                      <SmartCompanySearch
                        value={pasteForm.company_id ? (assignedCompanies.find(c => (c.company_id||c.id) === pasteForm.company_id)?.name || '') : ''}
                        suggested={assignedCompanies}
                        onSelect={co => setPasteForm(p => ({ ...p, company_id: co?.id || null }))}
                        onClear={() => setPasteForm(p => ({ ...p, company_id: null }))}
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <EyebrowLabel>Assign User</EyebrowLabel>
                      <SmartUserSearch
                        value={pasteForm.user_ids[0] ? (allUsers.find(u => u.id === pasteForm.user_ids[0])?.display_name || '') : ''}
                        suggested={assignedUsers} allUsers={allUsers}
                        onSelect={u => setPasteForm(p => ({ ...p, user_ids: [u.id] }))}
                        onClear={() => setPasteForm(p => ({ ...p, user_ids: [] }))}
                      />
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <EyebrowLabel>Default Subject (if not detected)</EyebrowLabel>
                    <Input size="md" value={pasteForm.subject} placeholder="Email chain"
                      onChange={e => setPasteForm(p => ({ ...p, subject: e.target.value }))} />
                  </FormControl>
                  <FormControl>
                    <EyebrowLabel>Paste Email Content</EyebrowLabel>
                    <Textarea size="md" rows={14} value={pasteForm.raw_text}
                      onChange={e => setPasteForm(p => ({ ...p, raw_text: e.target.value }))}
                      placeholder="Paste the full email chain here…" fontFamily="mono" fontSize="13px" />
                  </FormControl>

                  {/* Attachments */}
                  <FormControl>
                    <EyebrowLabel>Attachments</EyebrowLabel>
                    <input type="file" multiple ref={pasteFileRef} style={{ display: 'none' }}
                      onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) setPasteFiles(p => [...p, ...fs]); e.target.value = ''; }} />
                    <Box
                      onClick={() => pasteFileRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); }}
                      onDrop={e => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) setPasteFiles(p => [...p, ...fs]); }}
                      cursor="pointer" borderRadius="md" border="2px dashed" borderColor="purple.300"
                      bg="white" _hover={{ borderColor: 'purple.500', bg: 'purple.50' }} px={4} py={5} textAlign="center">
                      <HStack justify="center" color="purple.500" spacing={2}>
                        <FiPaperclip />
                        <Text fontSize="13px" fontWeight="600">Drag &amp; drop files here, or click to add</Text>
                      </HStack>
                      <Text fontSize="10px" color="gray.400" mt={1}>Multiple files supported — saved to this task's documents.</Text>
                    </Box>
                    {pasteFiles.length > 0 && (
                      <VStack align="stretch" spacing={1} mt={2}>
                        {pasteFiles.map((f, i) => (
                          <HStack key={i} justify="space-between" bg="white" borderRadius="md" px={3} py={1.5} border="1px" borderColor="gray.200">
                            <HStack minW={0} spacing={2}>
                              <FiFile size={13} />
                              <Text fontSize="12px" isTruncated>{f.name}</Text>
                              <Text fontSize="10px" color="gray.400" flexShrink={0}>{(f.size / 1024).toFixed(0)} KB</Text>
                            </HStack>
                            <IconButton size="xs" variant="ghost" colorScheme="red" aria-label="Remove file"
                              icon={<FiX />} onClick={() => setPasteFiles(p => p.filter((_, idx) => idx !== i))} />
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </FormControl>

                  <Flex w="100%" justify="space-between" align="center" pt={1}>
                    <Text fontSize="11px" color="gray.500">
                      {pasteForm.raw_text.length > 0 ? `${pasteForm.raw_text.length} chars` : 'Waiting…'}
                      {pasteFiles.length > 0 ? ` · ${pasteFiles.length} file${pasteFiles.length === 1 ? '' : 's'}` : ''}
                    </Text>
                    <Button size="md" bg="purple.600" color="white" _hover={{ bg: 'purple.700' }}
                      onClick={handlePaste} isLoading={pasting} loadingText="Parsing…">Parse &amp; Import</Button>
                  </Flex>
                </VStack>
              </Box>
            )}

            {/* Detail / edit */}
            {selectedComm && !showForm && !showPaste && (
              <Box bg="gray.50" p={4} borderRadius="md" mb={4} ref={commDetailRef} maxH="60vh" overflowY="auto">
                <Flex justify="space-between" mb={3}>
                  <HStack spacing={1}>
                    <Button size="xs" variant="ghost" onClick={() => { setSelectedComm(null); setEditForm(null); }}>← Back</Button>
                    {commIndex >= 0 && (
                      <HStack spacing={0} ml={2}>
                        <IconButton icon={<FiChevronUp />} size="xs" variant="ghost" isDisabled={commIndex <= 0} onClick={() => navigateComm(-1)} aria-label="Prev" />
                        <Text fontSize="9px" color="gray.400" minW="40px" textAlign="center">{commIndex+1} / {comms.length}</Text>
                        <IconButton icon={<FiChevronDown />} size="xs" variant="ghost" isDisabled={commIndex >= comms.length-1} onClick={() => navigateComm(1)} aria-label="Next" />
                      </HStack>
                    )}
                  </HStack>
                  {!editForm ? (
                    <HStack spacing={1}>
                      {!selectedComm.is_inbox_email && <>
                        <Button size="xs" colorScheme="blue" variant="outline" onClick={() => startEdit(selectedComm)}>Edit</Button>
                        <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                          onClick={() => { setDeleteConfirm(selectedComm); setDeleteReason(''); }} aria-label="Delete" />
                      </>}
                      {selectedComm.is_inbox_email && <Badge colorScheme="purple" fontSize="9px">📥 From Email Inbox</Badge>}
                    </HStack>
                  ) : (
                    <HStack spacing={1}>
                      <Button size="xs" variant="ghost" onClick={() => setEditForm(null)}>Cancel</Button>
                      <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }} onClick={handleUpdate} isLoading={saving}>Save</Button>
                    </HStack>
                  )}
                </Flex>

                {!editForm ? (
                  <VStack spacing={2} align="stretch" cursor={selectedComm.is_inbox_email ? "default" : "pointer"} onClick={() => !selectedComm.is_inbox_email && startEdit(selectedComm)}>
                    <HStack fontSize="10px" color="gray.500" spacing={3} flexWrap="wrap">
                      <Text>{formatDateTime(selectedComm)}</Text>
                      <Badge colorScheme={methodColors[selectedComm.method]} fontSize="9px">{ml(selectedComm.method)}</Badge>
                      <Badge variant="outline" fontSize="9px" colorScheme={selectedComm.direction === 'inbound' ? 'green' : 'blue'}>{selectedComm.direction}</Badge>
                      {selectedComm.purpose_name && <Badge colorScheme="yellow" fontSize="9px">{selectedComm.purpose_name}</Badge>}
                      {selectedComm.sub_task_title && <Badge colorScheme="orange" fontSize="9px">↳ Sub-task: {selectedComm.sub_task_title}</Badge>}
                    </HStack>
                    <Text fontSize="sm" fontWeight="600">{selectedComm.subject || '(no subject)'}</Text>
                    {selectedComm.is_inbox_email && selectedComm.from_address && (
                      <Text fontSize="10px" color="purple.600">From: {selectedComm.from_name ? `${selectedComm.from_name} <${selectedComm.from_address}>` : selectedComm.from_address}</Text>
                    )}
                    {contactDisplay(selectedComm) && <Text fontSize="11px" color="gray.600">{contactDisplay(selectedComm)}</Text>}
                    <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap"
                      overflowWrap="anywhere">
                      {/* Replace non-breaking spaces with normal ones so
                          Outlook-pasted paragraphs wrap at word boundaries
                          instead of relying on emergency mid-word breaks. */}
                      {String(selectedComm.body || '(no content)').replace(/\u00A0/g, ' ')}
                    </Text>
                    {selectedComm.created_by_name && <Text fontSize="10px" color="gray.400" mt={1}>Logged by {selectedComm.created_by_name}</Text>}
                    <Text fontSize="9px" color="blue.400" mt={1}>Click anywhere to edit</Text>
                  </VStack>
                ) : (
                  <VStack spacing={2}>
                    {renderMethodRow(editForm, setEditForm)}
                    {renderRecipientsSection(editForm, setEditForm)}
                    {(editForm.company_name || editForm.contact_name || editForm.purpose_name) && (
                      <Flex w="100%" gap={2} fontSize="10px" flexWrap="wrap">
                        {editForm.company_name && <Badge colorScheme="teal" fontSize="9px">{editForm.company_name}</Badge>}
                        {editForm.contact_name && <Badge colorScheme="purple" fontSize="9px">{editForm.contact_name}</Badge>}
                        {editForm.purpose_name && <Badge colorScheme="yellow" fontSize="9px">{editForm.purpose_name}</Badge>}
                        <Text cursor="pointer" color="red.400" onClick={() => setEditForm(p => ({ ...p, company_id: null, company_name: '', contact_id: null, contact_name: '', purpose_id: null, purpose_name: '' }))}>clear</Text>
                      </Flex>
                    )}
                    <FormControl>
                      <EyebrowLabel>Subject</EyebrowLabel>
                      <Input size="xs" value={editForm.subject} onChange={e => setEditForm(p => ({ ...p, subject: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <EyebrowLabel>Content</EyebrowLabel>
                      <Textarea size="xs" rows={4} value={editForm.body} onChange={e => setEditForm(p => ({ ...p, body: e.target.value }))} />
                    </FormControl>
                  </VStack>
                )}
                <Box mt={3}>
                  {selectedComm.is_inbox_email ? (
                    // ── Inbox-email attachments ───────────────────────────
                    // Email attachments live on the email_messages record (not
                    // on the synthetic comm row). They're read-only here: the
                    // sender's files can't be deleted from our store from this
                    // view. Each one offers download + a per-file save to docs.
                    <Box>
                      <Flex align="center" justify="space-between" mb={1}>
                        <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em"
                          bg="gray.800" color="white" px={1.5} py={0.5} borderRadius="sm" display="inline-block">
                          <FiPaperclip size={9} style={{ display:'inline', marginRight:4 }} />
                          Attachments ({inboxAttachments.length})
                        </Text>
                        {inboxAttachments.length > 0 && (
                          <Button size="xs" leftIcon={<FiSave />} variant="outline" colorScheme="brand"
                            isLoading={savingToDocs}
                            onClick={() => {
                              if (inboxAttachments.length === 1) prefillFromFile(inboxAttachments[0]);
                              else setAttachChooser(inboxAttachments);
                            }}>
                            Save to documents
                          </Button>
                        )}
                      </Flex>
                      {inboxAttachments.length === 0 ? (
                        <Text fontSize="11px" color="gray.400">No attachments on this email.</Text>
                      ) : (
                        <Wrap spacing={1}>
                          {inboxAttachments.map(f => (
                            <WrapItem key={f.id}>
                              <HStack spacing={0} bg="white" border="1px" borderColor="gray.200"
                                borderRadius="md" px={2} py={1} fontSize="11px">
                                <FiFile size={11} color="#718096" />
                                <Text ml={1} maxW="220px" isTruncated title={f.original_name}>{f.original_name}</Text>
                                <Tooltip label="Download" fontSize="xs">
                                  <IconButton icon={<FiDownload />} size="xs" variant="ghost" aria-label="Download"
                                    onClick={() => window.open('/api/files/' + f.folder + '/' + f.stored_name, '_blank')} />
                                </Tooltip>
                              </HStack>
                            </WrapItem>
                          ))}
                        </Wrap>
                      )}
                    </Box>
                  ) : (
                    // ── Regular communication: existing FileUpload + a
                    // "Save attachments to documents" affordance. The
                    // FileUpload component owns its own fetch of /files;
                    // the save button hits our copy-files-to-documents
                    // endpoint which reads attachments from the comm.
                    <Box>
                      <FileUpload entityType="communication" entityId={selectedComm.id} folder="communications"
                        accept="image/*,.pdf,video/*,.doc,.docx,.xls,.xlsx" label="Attachments" />
                      <Flex justify="flex-end" mt={1}>
                        <Button size="xs" leftIcon={<FiSave />} variant="ghost" colorScheme="brand"
                          isLoading={savingToDocs}
                          onClick={() => openSaveToDocuments()}>
                          Save attachments to documents
                        </Button>
                      </Flex>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* List */}
            {!selectedComm && !showForm && (
              comms.length === 0
                ? <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No communications recorded yet.</Text>
                : (
                  <VStack spacing={1} align="stretch">
                    {comms.map(comm => (
                      <Box key={comm.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                        cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setSelectedComm(comm)}>
                        <Flex align="center" gap={2} fontSize="12px">
                          <Text fontWeight="500" color="gray.500" w="105px" flexShrink={0} fontSize="10px">{formatDateTime(comm)}</Text>
                          <Badge colorScheme={methodColors[comm.method]} fontSize="8px">{ml(comm.method)}</Badge>
                          <Badge variant="outline" fontSize="8px" colorScheme={comm.direction === 'inbound' ? 'green' : 'blue'}>{comm.direction === 'inbound' ? '←' : '→'}</Badge>
                          {comm.purpose_name && <Badge colorScheme="yellow" fontSize="8px">{comm.purpose_name}</Badge>}
                          {comm.assigned_user_name && <Badge colorScheme="teal" fontSize="8px">{comm.assigned_user_name}</Badge>}
                          {comm.sub_task_title && <Badge colorScheme="orange" fontSize="8px" flexShrink={0}>↳ {comm.sub_task_title}</Badge>}
                          <Text fontWeight="500" noOfLines={1} flex={1} minW={0}>{comm.subject || '(no subject)'}</Text>
                          {comm.is_inbox_email && <Badge colorScheme="purple" fontSize="7px" flexShrink={0}>📥 Inbox</Badge>}
                        </Flex>
                        {contactDisplay(comm) && <Text fontSize="10px" color="gray.400" ml="105px" mt={0.5}>{contactDisplay(comm)}</Text>}
                      </Box>
                    ))}
                  </VStack>
                )
            )}
          </ModalBody>
          <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation — separate modal so it's not inside the main one */}
      {deleteConfirm && (
        <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} size="sm">
          <ModalOverlay /><ModalContent>
            <ModalHeader fontSize="md" color="red.600">Delete Communication</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="12px" mb={2}>Delete "{deleteConfirm.subject || 'this communication'}"?</Text>
              <Input size="sm" placeholder="Reason (optional)" value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" mr={2} onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button size="sm" colorScheme="red" onClick={handleDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
    
    {/* Pick which attachment to save (when several) */}
    <Modal isOpen={!!attachChooser} onClose={() => setAttachChooser(null)} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Save which attachment?</ModalHeader>
        <ModalBody pb={4}>
          <VStack align="stretch" spacing={1}>
            {(attachChooser || []).map((f, i) => (
              <Button key={f.id || f.uuid || i} size="sm" variant="outline" justifyContent="flex-start"
                leftIcon={<FiPaperclip size={12} />} onClick={() => prefillFromFile(f)}>
                <Text fontSize="12px" noOfLines={1}>{f.original_name || f.name}</Text>
              </Button>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>

    {/* Standard document form, file preselected — no re-upload */}
    {docPrefill && (
      <DocumentsModal isOpen={true} projectId={projectId} taskId={taskId || selectedComm?.task_id || null}
        initialDoc={docPrefill} onClose={() => setDocPrefill(null)}
        onOpenComms={() => {}} onOpenNotes={() => {}} onOpenInvoice={() => {}} />
    )}
    </Modal>
      )}
    </>
  );
}
